import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TipoUsuario } from 'src/app/core/enum/global.enum';
import { UsuarioEmpresaInterface, UsuarioRepresentanteLegalInterface } from 'src/app/core/models/auth.model';
import { IdleService } from 'src/app/core/services/idle.service';
import { GuardService } from 'src/app/core/services/guards.service';
import { globalConfig } from 'src/app/core/utils/globalConfig';
import { NotificacionPushService } from 'src/app/core/services/notificacionPush.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private route = inject(Router);
  private idleService = inject(IdleService);
  private guardService = inject(GuardService);
  private notificacionPushService = inject(NotificacionPushService);

  private sanitizer = inject(DomSanitizer);

  toastMessage: string | null = null;

  constructor() {}

  #stateIndicativo = signal<any>({
    indicativo: [],
  });

  #stateUsuario = signal<any>({
    usuario: {},
  });

  #stateTipoDocumento = signal<any>({
    tipoDocumento: [],
  });

  #stateDependencias = signal<any>({
    dependencias: [],
  });

  public indicativo = computed(() => this.#stateIndicativo().indicativo);
  public usuario: any = computed(() => this.#stateUsuario().usuario);
  public tipoDocumento: any = computed(() => this.#stateTipoDocumento().tipoDocumento);
  public dependencias: any = computed(() => this.#stateDependencias().dependencias);

  login(loginData: any) {
    return this.http.post('/auth-empresarial/login-usuario-empresarial', loginData);
  }

  preLogin(loginData: any) {
    return this.http.post('/auth-empresarial/pre-login-usuario-empresarial', loginData);
  }

  seleccionarEmpresa(body: any) {
    return this.http.post('/auth-empresarial/seleccionar-empresa', body);
  }

  registrarUsuarioEmpresa(
    usuarioEmpresaData: UsuarioEmpresaInterface,
    representanteLegalData?: UsuarioRepresentanteLegalInterface,
  ) {
    const payload = {
      usuarioEmpresa: {
        ...usuarioEmpresaData,
        tipo_usuario_id: usuarioEmpresaData.tipo_usuario_id,
      },
      ...(representanteLegalData && {
        usuarioEmpresarial: {
          nombres: representanteLegalData.nombresRepresentante,
          apellidos: representanteLegalData.apellidosRepresentante,
          tipoDocumento: representanteLegalData.tipoDocumentoRepresentante,
          identificacion: representanteLegalData.identificacionRepresentante,
          email: representanteLegalData.emailRepresentante,
          telefono: representanteLegalData.telefonoRepresentante,
          primerInicio: true,
          terminosCondiciones: true,
          tratamientoDatos: true,
          tipoUsuarioId: TipoUsuario.USUARIO_ADMINISTRADOR,
          direccion: '',
          pais: 52,
        },
      }),
    };

    return this.http.post('/usuario-cuenta-empresarial', payload);
  }

  recuperarPassword(body: any) {
    return this.http.post('/usuario-cuenta-empresarial/recuperar-password', body);
  }

  setToken(token: string) {
    sessionStorage.setItem(globalConfig.tokenApi, token);
  }

  logout() {
    this.route.navigate(['/auth-empresarial/sign-in']);
    this.idleService.stopWatching();
    this.toastMessage = null;
    this.notificacionPushService.disconnect();
    sessionStorage.removeItem(globalConfig.tokenApi);
    this.guardService.resetUsuarioData();
  }

  cambiarPasswordUsuarioFront(newPassword: string) {
    return this.http.patch('/usuario-cuenta-empresarial/nuevo-password', {
      password: newPassword,
    });
  }

  cambiarPasswordUsuarioFrontDashboard(newPassword: string, oldPassword: string = '') {
    const params = new HttpParams().set('oldPassword', oldPassword);
    return this.http.patch(
      `/usuario-cuenta-empresarial/nuevo-password`,
      {
        password: newPassword,
      },
      { params },
    );
  }

  getIndicativo() {
    this.http
      .get('/pais/indicativos')
      .pipe(
        map(({ data }: any) => {
          let filter = [];
          const { id, indicativo, nombre } = data.find((country: { nombre: string }) => country.nombre === 'Colombia');
          filter.push({
            id,
            name: `+ ${indicativo} ${nombre}`,
            indicativo,
          });
          const countriesWithoutColombia = data.filter((country: { nombre: string }) => country.nombre !== 'Colombia');
          for (const { id, indicativo, nombre } of countriesWithoutColombia) {
            filter.push({
              id,
              name: `+ ${indicativo} ${nombre}`,
              indicativo,
            });
          }
          return filter;
        }),
      )
      .subscribe((indicativo: any) => {
        this.#stateIndicativo.set({
          indicativo,
        });
      });
  }

  getTipoDocumento() {
    this.http
      .get('/tipo-documento')
      .pipe(
        map(({ data }: any) => {
          let filter = [];
          for (const { id, nombre } of data) {
            filter.push({
              id,
              name: nombre,
            });
          }
          return filter;
        }),
      )
      .subscribe((tipoDocumento: any) => {
        this.#stateTipoDocumento.set({
          tipoDocumento,
        });
      });
  }

  loadPdf(ruta: string) {
    this.http.get(ruta, { responseType: 'blob' }).subscribe((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      return this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
    });
  }

  recuperarPasswordUsuario(newPassword: string) {
    return this.http.patch('/usuario-cuenta-empresarial/update-recuperar-password', {
      password: newPassword,
    });
  }

  actualizarPerfil(body: any) {
    return this.http.patch('/usuario-cuenta-empresarial/actualizar-perfil', body);
  }

  setDependencias(dependencias: any) {
    const selectDependencia = dependencias.map((item: any) => {
      return item.id === 1
        ? { id: item.id, name: 'Sede Principal', nit: item.nit }
        : { id: item.id, name: `${item.nombre} (${item.codigo})`, nit: item.nit };
    });
    this.#stateDependencias.set({
      dependencias: selectDependencia,
    });
  }
}
